<template>
  <div class="container">
    <!-- ========== Header Section ========== -->
    <div>
      <h2 class="mt-4 mb-2">{{ $t('DSM-M13-02') }}</h2>
      <p class="mb-0">{{ $t('DSM-M13-03') }}</p>
    </div>
    <!-- ========== End Header Section ========== -->

    <!-- ========== Filter Section ========== -->
    <div class="my-4 d-flex flex-column flex-md-row">
      <div class="w-100 w-md-50 mb-2 mb-md-0 mr-0 mr-md-2">
        <b-dropdown id="dropdown-1" block variant="outline-secondary" class="dropdown-list" @hide="providerSearchKey = ''">
          <template #button-content>
            <div class="d-flex justify-content-between align-items-center font-weight-normal">
              <div class="overflow-hidden mr-3">
                <span v-if="selectedProviderId && loaded" class="selected-option">{{ $t(selectedProviderInfo.display_name) }}</span>
                <span v-else>{{ $t('DSM-M13-04') }}</span>
              </div>
              <div>
                <i class="fas fa-angle-down fa-sm input-icon-default" />
                <i class="fas fa-angle-up fa-sm input-icon-active" />
              </div>
            </div>
          </template>
          <b-dropdown-form class="mx-2 mb-1">
            <b-form-input v-model="providerSearchKey" :placeholder="`${$t('DSCC06-02-01')} ${$t('DSM-M13-04')}`" />
            <div v-if="filteredProviderList.length === 0" class="pt-3 text-center not-found-message">{{ $t('err-general-01-02') }}</div>
          </b-dropdown-form>
          <b-dropdown-item v-for="provider in filteredProviderList" :key="provider.id" @click="selectedProviderId = provider.id">{{ $t(provider.display_name) }}</b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="w-100 w-md-50 ml-0 ml-md-2">
        <b-dropdown id="dropdown-1" block variant="outline-secondary" class="dropdown-list" @hide="developerSearchKey = ''">
          <template #button-content>
            <div class="d-flex justify-content-between align-items-center font-weight-normal">
              <div class="overflow-hidden mr-3">
                <span v-if="selectedDeveloperId && loaded" class="selected-option">{{ $t(selectedDeveloperInfo.display_name) }}</span>
                <span v-else>{{ $t('DSM-M13-05') }}</span>
              </div>
              <div>
                <i class="fas fa-angle-down fa-sm input-icon-default" />
                <i class="fas fa-angle-up fa-sm input-icon-active" />
              </div>
            </div>
          </template>
          <b-dropdown-form class="mx-2 mb-1">
            <b-form-input v-model="developerSearchKey" :placeholder="`${$t('DSCC06-02-01')} ${$t('DSM-M13-05')}`" />
            <div v-if="filteredDeveloperList.length === 0" class="pt-3 text-center not-found-message">{{ $t('err-general-01-02') }}</div>
          </b-dropdown-form>
          <b-dropdown-item v-for="developer in filteredDeveloperList" :key="developer.id" @click="selectedDeveloperId = developer.id">{{ $t(developer.display_name) }}</b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
    <!-- ========== End Filter Section ========== -->

    <!-- ========== Central Table Section ========== -->
    <div>
      <table class="table table-bordered table-md-hover w-100 mx-auto mt-3 mx-10 central-table">
        <thead class="thead-light">
          <tr>
            <th scope="col" class="d-md-none align-middle"><span v-for="(field, index) in centralTableField" :key="field.value_field">{{ $t(field.name) }}<span v-if="index !== centralTableField.length - 1">, </span></span></th>
            <th v-for="field in centralTableField" :key="field.value_field" scope="col" class="d-none d-md-table-cell align-middle" :class="field.style">{{ $t(field.name) }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="!loaded">
            <tr v-for="(index) in (3)" :key="index">
              <td :id="`central-table-row-${index}`" class="td d-md-none">
                <div v-b-toggle="`record-${index}`" data-toggle="collapse" class="link-collapse" aria-expanded="false" :aria-controls="`record-${index}`">
                  <div class="link-collapse-default w-100">
                    <div class="d-flex flex-row justify-content-between">
                      <div class="w-90">
                        <div v-for="field in centralTableField" v-show="field.is_show_in_short_table" :key="field.value_field" class="d-flex flex-row mb-3">
                          <div class="min-w-4rem text-center mr-1">
                            <i class="fa-sm central-table-icon" :class="field.icon" />
                          </div>
                          <div>
                            <div v-if="field.type === 'link-text'">
                              <SkeletonUI type="span" class="w-100" />
                            </div>
                            <div v-if="field.type === 'list'">
                              <div v-for="(field_item, field_index) in field.list_item" :key="field_item.field" :class="field_item.style">
                                <SkeletonUI type="span" :class="{ 'mb-1': field_index !== field.list_item.length - 1 }" class="w-100" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <i class="fas fa-angle-down fa-sm central-table-icon" />
                    </div>
                  </div>
                  <div class="link-collapse-active w-100">
                    <div class="d-flex flex-row justify-content-between">
                      <div class="w-90">
                        <div v-for="field in centralTableField" :key="field.value_field" class="d-flex flex-row mb-3">
                          <div class="min-w-4rem text-center mr-1">
                            <i class="fa-sm central-table-icon" :class="field.icon" />
                          </div>
                          <div>
                            <div class="font-weight-bold mb-1">{{ $t(field.name) }}</div>
                            <div>
                              <div v-if="field.type === 'link-text'">
                                <SkeletonUI type="span" />
                              </div>
                              <div v-if="field.type === 'list'">
                                <div v-for="(field_item, field_index) in field.list_item" :key="field_item.field" :class="field_item.style">
                                  <SkeletonUI type="span" :class="{ 'mb-1': field_index !== field.list_item.length - 1 }" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <i class="fas fa-angle-up fa-sm central-table-icon" />
                    </div>
                  </div>
                </div>
                <b-collapse :id="`record-${index}`" :accordion="`central-table-row-${index}`" />
              </td>
              <td v-for="field in centralTableField" :key="field.value_field" scope="col" class="d-none d-md-table-cell">
                <div>
                  <div v-if="field.type === 'link-text'">
                    <SkeletonUI type="span" />
                  </div>
                  <div v-if="field.type === 'list'">
                    <div v-for="(field_item, field_index) in field.list_item" :key="field_item.field" :class="field_item.style">
                      <SkeletonUI type="span" :class="{ 'mb-1': field_index !== field.list_item.length - 1 }" />
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
          <template v-else-if="centralTableList.length > 0">
            <tr v-for="(record, index) in centralTableList" :key="index">
              <td :id="`central-table-row-${index}`" class="td d-md-none">
                <div v-b-toggle="`record-${index}`" data-toggle="collapse" class="link-collapse" aria-expanded="false" :aria-controls="`record-${index}`">
                  <div class="link-collapse-default w-100">
                    <div class="d-flex flex-row justify-content-between">
                      <div class="w-90">
                        <div v-for="field in centralTableField" v-show="field.is_show_in_short_table" :key="field.value_field" class="d-flex flex-row mb-3">
                          <div class="min-w-4rem text-center mr-1">
                            <i class="fa-sm central-table-icon" :class="field.icon" />
                          </div>
                          <div>
                            <div v-if="field.type === 'link-text'">
                              <div v-if="record[field.link_field]"><a :href="record[field.link_field]" target="_blank">{{ record[field.value_field] }}</a></div>
                              <div v-else>{{ record[field.value_field] }}</div>
                            </div>
                            <div v-if="field.type === 'list'">
                              <div v-for="(item, item_index) in record[field.value_field]" :key="item_index" :class="{ 'mb-2': item_index !== record[field.value_field].length - 1 }">
                                <div v-for="field_item in field.list_item" :key="field_item.field" :class="field_item.style">{{ item[field_item.field] }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <i class="fas fa-angle-down fa-sm central-table-icon" />
                    </div>
                  </div>
                  <div class="link-collapse-active w-100">
                    <div class="d-flex flex-row justify-content-between">
                      <div class="w-90">
                        <div v-for="field in centralTableField" :key="field.value_field" class="d-flex flex-row mb-3">
                          <div class="min-w-4rem text-center mr-1">
                            <i class="fa-sm central-table-icon" :class="field.icon" />
                          </div>
                          <div>
                            <div class="font-weight-bold mb-1">{{ $t(field.name) }}</div>
                            <div>
                              <div v-if="field.type === 'link-text'">
                                <div v-if="record[field.link_field]"><a :href="record[field.link_field]" target="_blank">{{ record[field.value_field] }}</a></div>
                                <div v-else>{{ record[field.value_field] }}</div>
                              </div>
                              <div v-if="field.type === 'list'">
                                <div v-for="(item, item_index) in record[field.value_field]" :key="item_index" :class="{ 'mb-2': item_index !== record[field.value_field].length - 1 }">
                                  <div v-for="field_item in field.list_item" :key="field_item.field" :class="field_item.style">{{ item[field_item.field] }}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <i class="fas fa-angle-up fa-sm central-table-icon" />
                    </div>
                  </div>
                </div>
                <b-collapse :id="`record-${index}`" :accordion="`central-table-row-${index}`" />
              </td>
              <td v-for="field in centralTableField" :key="field.value_field" scope="col" class="d-none d-md-table-cell">
                <div>
                  <div v-if="field.type === 'link-text'">
                    <div v-if="record[field.link_field]"><a :href="record[field.link_field]" target="_blank">{{ record[field.value_field] }}</a></div>
                    <div v-else>{{ record[field.value_field] }}</div>
                  </div>
                  <div v-if="field.type === 'list'">
                    <div v-for="(item, item_index) in record[field.value_field]" :key="item_index" :class="{ 'mb-3': item_index !== record[field.value_field].length - 1 }">
                      <div v-for="field_item in field.list_item" :key="field_item.field" :class="field_item.style">{{ item[field_item.field] }}</div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="4" class="text-center no-search-result">{{ $t('DSM-M13-10') }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <!-- ========== End Central Table Section ========== -->

    <!-- === Pagination -->
    <div v-if="(numOfRecords > 0)" aria-label="Page navigation example" class="space-top-2">
      <Pagination
        :page="currentPage"
        :page-number="totalPageNum"
        :page-change-handler="pageChange"
      />
    </div>
    <!-- === End Pagination -->

    <div class="space-bottom-2" />

  </div>
</template>

<script>
import Pagination from '@/components/pagination'
import SkeletonUI from '@/components/skeleton-ui'
import getResourcePath from '@/utils/get-resource-path'
import { getCentralRegisterList } from '@/api/central-register-list'
import { searchDeveloper } from '@/api/developer'
import { searchProvider } from '@/api/provider'
import { mapGetters } from 'vuex'

export default {
  name: 'CentralRegisterList',
  components: {
    Pagination,
    SkeletonUI
  },
  data() {
    return {
      loaded: false,
      centralTableLoaded: false,
      selectedDeveloperId: 'all',
      developerSearchKey: '',
      defaultDeveloper: [{ id: 'all', display_name: 'DSM-M13-05-01' }],
      developerList: [],
      filteredDeveloperList: [],
      selectedProviderId: 'all',
      providerSearchKey: '',
      defaultProvider: [{ id: 'all', display_name: 'DSM-M13-04-01' }],
      providerList: [],
      filteredProviderList: [],
      centralTableField: [
        {
          name: 'DSM-M13-06',
          id_field: 'provider_id',
          value_field: 'provider_display_name',
          link_field: 'provider_website',
          style: 'w-20',
          icon: 'fas fa-landmark',
          type: 'link-text',
          is_show_in_short_table: true
        },
        {
          name: 'DSM-M13-07',
          id_field: 'tsp_id',
          value_field: 'tsp_display_name',
          link_field: 'tsp_website',
          style: 'w-20',
          icon: 'fas fa-building',
          type: 'link-text',
          is_show_in_short_table: true
        },
        {
          name: 'DSM-M13-08',
          id_field: 'project_id',
          value_field: 'project_name',
          link_field: 'project_product_url',
          style: 'w-20',
          icon: 'fas fa-briefcase',
          type: 'link-text',
          is_show_in_short_table: true
        },
        {
          name: 'DSM-M13-09',
          value_field: 'apis',
          style: 'w-40',
          icon: 'fas fa-code',
          type: 'list',
          list_item: [
            {
              field: 'api_name',
              style: ''
            },
            {
              field: 'brief_description',
              style: 'small'
            }
          ],
          is_show_in_short_table: true
        }
      ],
      centralTableList: [],
      pageSize: 20,
      numOfRecords: 0,
      currentPage: 1
    }
  },
  computed: {
    selectedDeveloperInfo() {
      return this.developerList.find(developer => developer.id === this.selectedDeveloperId)
    },
    selectedProviderInfo() {
      return this.providerList.find(provider => provider.id === this.selectedProviderId)
    },
    tableOffset() {
      return (this.currentPage - 1) * this.pageSize
    },
    totalPageNum() {
      return Math.ceil(this.numOfRecords / this.pageSize)
    },
    ...mapGetters(['portalType'])
  },
  watch: {
    async selectedDeveloperId() {
      await this.pageChange(1)
    },
    async selectedProviderId() {
      await this.pageChange(1)
    },
    developerSearchKey() {
      this.localSearchDeveloper()
    },
    providerSearchKey() {
      this.localSearchProvider()
    }
  },
  async created() {
    this.loaded = false
    await this.getDevelopersList()
    await this.getProvidersList()
    await this.pageChange(1)
    this.loaded = true
  },
  async mounted() {
    // new HSMegaMenu($('.js-mega-menu')).init()
    // $('.dropdown-toggle').dropdown()
  },
  methods: {
    async getDevelopersList() {
      await searchDeveloper(this.portalType).then(res => {
        this.developerList = this.defaultDeveloper.concat(res['data'].map(developer => {
          return {
            ...developer,
            developer_id: developer.id,
            display_name: `${developer.tsp_name} ${developer.tsp_name_chinese}`,
            profile_picture_url: getResourcePath('developer_logo', developer)
          }
        }))
      }).catch(err => {
        console.log(err)
        this.developerList = []
      })
      this.localSearchDeveloper()
    },
    async getProvidersList() {
      await searchProvider(this.portalType).then(res => {
        this.providerList = this.defaultProvider.concat(res['data'].map(provider => ({
          ...provider,
          provider_id: provider.id,
          display_name: provider.bank_name,
          profile_picture_url: getResourcePath('provider_logo', provider)
        })))
      }).catch(err => {
        console.log(err)
        this.providerList = []
        this.$store.dispatch('app/setLoading', false)
      })
      this.localSearchProvider()
    },
    async getCentralList() {
      this.centralTableLoaded = false
      var filter = {
        industry: this.portalType
      }
      if (this.selectedDeveloperId !== '' && this.selectedDeveloperId !== 'all') {
        filter.developerId = this.selectedDeveloperId
      }
      if (this.selectedProviderId !== '' && this.selectedProviderId !== 'all') {
        filter.providerId = this.selectedProviderId
      }
      await getCentralRegisterList(this.pageSize, this.tableOffset, filter).then(res => {
        this.centralTableList = res['data'].map(record => {
          return {
            ...record,
            developer: record.id,
            provider_display_name: record.provider_name,
            tsp_display_name: `${record.tsp_name} ${record.tsp_name_chinese}`,
            apis: record.services.filter(service => service.bank_id === record.provider_id)
          }
        })
        this.numOfRecords = res.headers && res.headers['content-range'] ? parseInt(res.headers['content-range'].split('/')[1]) : 0
        this.centralTableLoaded = true
      }).catch(err => {
        console.log(err)
        this.centralTableList = []
        this.centralTableLoaded = true
        this.$store.dispatch('app/setLoading', false)
      })
    },
    localSearchDeveloper() {
      const key = this.developerSearchKey ? this.developerSearchKey.toLowerCase() : ''
      const filteredDeveloperKey = key.split(/[~`!@#$%^&*\(\)\-\_\=\[\]\{\};;\'\"/?.>,<\s]/g).filter(e => e)
      this.filteredDeveloperList = this.developerList.filter(i => !filteredDeveloperKey || filteredDeveloperKey.every(element => {
        const name = this.$t(i.display_name).toLowerCase()
        const filteredName = name.split(/[~`!@#$%^&*\(\)\-\_\=\[\]\{\};;\'\"/?.>,<\s]/g).filter(e => e)
        return filteredName.includes(element)
      }))
    },
    localSearchProvider() {
      const key = this.providerSearchKey ? this.providerSearchKey.toLowerCase() : ''
      const filteredProviderKey = key.split(/[~`!@#$%^&*\(\)\-\_\=\[\]\{\};;\'\"/?.>,<\s]/g).filter(e => e)
      this.filteredProviderList = this.providerList.filter(i => !filteredProviderKey || filteredProviderKey.every(element => {
        const name = this.$t(i.display_name).toLowerCase()
        const filteredName = name.split(/[~`!@#$%^&*\(\)\-\_\=\[\]\{\};;\'\"/?.>,<\s]/g).filter(e => e)
        return filteredName.includes(element)
      }))
    },
    async pageChange(val) {
      this.currentPage = val
      await this.getCentralList()
    }
  }
}
</script>

<style lang="scss" scoped>
.card-title {
  font-size: 18px;
}
.home-card {
  box-shadow: 0px 12px 15px rgb(140 152 164 / 10%);
  cursor: pointer;
}
.card-text-button {
  color: #57abf0;

  &:hover {
    color: #007DBA;
  }
}
.field-title {
    color: #71869D;
    font-size: 14px;
}
.central-table {
    .td:has(div[aria-expanded="true"]) {
        background-color: #f8fafd;
    }
}
.central-table-icon {
  margin-top: 6px;
}
@media (min-width: 768px) {
  .w-md-70 {
     width: 70% !important;
  }
}
::v-deep .dropdown-list {
  .btn-outline-secondary:hover, .btn-outline-secondary:focus, .btn-outline-secondary.dropdown-toggle {
    color: #71869d;
    background-color: #FFFFFF;
    border-color: #e7eaf3;
  }
  .dropdown-menu {
    width: 100%;
  }
}
.selected-option {
  color: #000000;
}
.dropdown-toggle[aria-expanded="true"] {
  .input-icon-default {
    display: none;
  }
  .input-icon-active {
    display: block;
  }
}
.dropdown-toggle[aria-expanded="false"] {
  .input-icon-default {
    display: block;
  }
  .input-icon-active {
    display: none;
  }
}
::v-deep .dropdown-menu {
  max-height: 250px;
  overflow-y: scroll;
}
::v-deep .dropdown-item {
  white-space: break-spaces;
}
.not-found-message {
  color: #999;
  font-size: 14px;
}
.no-search-result {
  padding: 32px 0px;
}
</style>
